import { useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../../global/contexts/auth/auth';
import { IconChevronLeft, IconLogout } from '../../common/icons';
import { NAV_SECTIONS } from './nav-items';

import './styles/sidebar.css';

// TSS vite/05 — the sidebar is a permission-filtered menu builder. can() is
// used OUTSIDE JSX here to compute the visible sections; JSX gating elsewhere
// uses <Gate>. Active state via <NavLink>. El rail se puede colapsar a solo
// iconos (persistido en localStorage); en móvil siempre va expandido (drawer).

const COLLAPSE_KEY = 'cowre:sidebar-collapsed';

function readCollapsed() {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1'; }
    catch { return false; }
}

function isVisible(item, can) {
    if (item.domain) return can(item.domain, item.action);
    return true;
}

export default function Sidebar({ onNavigate }) {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { user, can, logout } = useAuth();
    const [collapsed, setCollapsed] = useState(readCollapsed);

    const toggleCollapsed = () => setCollapsed((prev) => {
        const next = !prev;
        try { localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0'); } catch { /* ignore */ }
        return next;
    });

    // Un ítem con `activePaths` (recurso con pestañas) queda resaltado también en las
    // rutas de sus pestañas (p.ej. Animales sigue activo en /admin/semen).
    const extraActive = (item) => (item.activePaths || []).some((p) => pathname === p || pathname.startsWith(`${p}/`));

    const sections = useMemo(() => (
        NAV_SECTIONS
            .map(section => ({ ...section, items: section.items.filter(item => isVisible(item, can)) }))
            .filter(section => section.items.length > 0)
    ), [can]);

    const handleLogout = async () => {
        await logout();
        navigate('/login', { replace: true });
    };

    const initials = (user?.name || user?.username || user?.email || '?')
        .trim().charAt(0).toUpperCase();

    return (
        <aside className={`sidebar ${collapsed ? 'sidebar--collapsed' : ''}`}>
            <div className="sidebar__brand">
                <span className="sidebar__logo-tile">
                    <img className="sidebar__logo" src="/cowre.png" alt="" aria-hidden="true" />
                </span>
                <span className="sidebar__wordmark">COWRE</span>
                <button
                    type="button"
                    className="sidebar__collapse"
                    onClick={toggleCollapsed}
                    aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'}
                    aria-pressed={collapsed}
                    title={collapsed ? 'Expandir menú' : 'Colapsar menú'}
                >
                    <IconChevronLeft />
                </button>
            </div>

            <nav className="sidebar__nav">
                {sections.map((section, i) => (
                    <div key={section.title || `s-${i}`} className="sidebar__section">
                        {section.title && (
                            <span className="sidebar__section-title">{section.title}</span>
                        )}
                        <ul className="sidebar__list">
                            {section.items.map(item => (
                                <li key={item.to}>
                                    <NavLink
                                        to={item.to}
                                        onClick={onNavigate}
                                        title={item.label}
                                        className={({ isActive }) =>
                                            `sidebar__link ${isActive || extraActive(item) ? 'sidebar__link--active' : ''}`
                                        }
                                    >
                                        <span className="sidebar__link-icon">{item.icon}</span>
                                        <span className="sidebar__link-label">{item.label}</span>
                                    </NavLink>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </nav>

            <div className="sidebar__footer">
                <div className="sidebar__user">
                    <span className="sidebar__avatar">{initials}</span>
                    <span className="sidebar__user-meta">
                        <span className="sidebar__user-name">{user?.name || user?.username || '—'}</span>
                        {user?.role && <span className="sidebar__user-role">{user.role}</span>}
                    </span>
                </div>
                <button type="button" className="sidebar__logout" onClick={handleLogout} title="Cerrar sesión">
                    <IconLogout />
                    <span>Cerrar sesión</span>
                </button>
            </div>
        </aside>
    );
}
